import React, { useState } from 'react';
import { Loader2, CalendarX2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { oapi, fmtWhen } from '@/lib/officialsApi';
import { AssignmentCard } from './AssignmentCard';

const Group = ({ title, items, onRespond, muted, testId }) => (items.length ? (
  <section className="space-y-3" data-testid={testId}>
    <h3 className="text-sm font-bold uppercase tracking-wide text-slate-500">{title} <span className="text-slate-400">({items.length})</span></h3>
    {items.map(a => <AssignmentCard key={a.id} a={a} onRespond={onRespond} muted={muted} />)}
  </section>
) : null);

const DeclineDialog = ({ a, busy, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  return (
    <Dialog open={!!a} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="portal-decline-dialog">
        <DialogHeader>
          <DialogTitle className="text-red-600">Decline this game?</DialogTitle>
          <DialogDescription>{a && `${a.game?.team_a_name} vs ${a.game?.team_b_name} · ${fmtWhen(a.game)}`}. Your assigner is told right away.</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional), e.g. work conflict" data-testid="portal-decline-reason" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Back</Button>
          <Button disabled={busy} onClick={() => onConfirm(reason)} className="bg-red-600 hover:bg-red-700" data-testid="portal-decline-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Decline
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PortalAssignments = ({ me, reload }) => {
  const [declining, setDeclining] = useState(null);
  const [busy, setBusy] = useState(false);
  const respond = async (a, action, reason = '') => {
    setBusy(true);
    try {
      await oapi(`/officials/portal/assignments/${a.id}/respond`, { method: 'POST', body: { action, reason } });
      toast.success(action === 'accept' ? 'Accepted. See you at the game!' : 'Declined. Your assigner has been told.');
      setDeclining(null);
      reload();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const onRespond = (a, action) => (action === 'accept' ? respond(a, 'accept') : setDeclining(a));
  const by = (s) => me.upcoming.filter(a => a.status === s);
  const nothing = !me.upcoming.length && !me.cancelled.length;

  return (
    <div className="space-y-8" data-testid="portal-assignments">
      {nothing && (
        <div className="bg-white border rounded-2xl py-12 px-6 text-center" data-testid="portal-no-assignments">
          <CalendarX2 className="w-8 h-8 mx-auto text-slate-300" />
          <p className="mt-3 font-semibold text-slate-700">No upcoming games yet</p>
          <p className="text-sm text-slate-500">Keep your availability up to date so your league can schedule you.</p>
        </div>
      )}
      <Group title="Waiting for your answer" items={by('pending')} onRespond={onRespond} testId="portal-group-pending" />
      <Group title="Confirmed" items={by('accepted')} onRespond={onRespond} testId="portal-group-accepted" />
      <Group title="Declined" items={by('declined')} muted testId="portal-group-declined" />
      <Group title="Cancelled" items={me.cancelled} muted testId="portal-group-cancelled" />
      {me.past.length > 0 && (
        <details className="group" data-testid="portal-group-past">
          <summary className="cursor-pointer text-sm font-bold uppercase tracking-wide text-slate-500">Past games ({me.past.length})</summary>
          <div className="space-y-3 mt-3">{me.past.slice(0, 30).map(a => <AssignmentCard key={a.id} a={a} muted />)}</div>
        </details>
      )}
      <DeclineDialog key={declining?.id} a={declining} busy={busy} onClose={() => setDeclining(null)} onConfirm={(r) => respond(declining, 'decline', r)} />
    </div>
  );
};
